import axiosInstance from './axios';

export interface SummaryCard {
  benchmark: string;
  label: string;
  status?: 'OK' | 'WARNING' | 'CRITICAL';
  trend: 'UP' | 'DOWN' | 'FLAT';
  unit: string;
  value: number;
}

export interface IntegrityScore {
  score: number;
  trendChange: number;
  trendDirection: 'UP' | 'DOWN' | 'FLAT';
}

export interface MetricItem {
  displayOverride: string | null;
  label: string;
  status: 'OK' | 'WARNING' | 'CRITICAL';
  unit: string;
  value: number | null;
}

export interface MarginIntegritySection {
  actualGrossMargin: MetricItem;
  contributionMargin: MetricItem;
  marginVariance: MetricItem;
  subtitle: string;
  targetGrossMargin: MetricItem;
  title: string;
}

export interface LaborPressureSection {
  laborPercentOfRevenue: MetricItem;
  laborRateVariance: MetricItem;
  projectedRateVariance?: MetricItem;
  revenuePerLaborHour: MetricItem;
  subtitle: string;
  supervisorCost?: MetricItem;
  supervisorRateVariance?: MetricItem;
  title: string;
  unbudgetedLaborHours: MetricItem;
}

export interface OperationalLeakageSection {
  costPerVisit: MetricItem;
  revenuePerVisit: MetricItem;
  subtitle: string;
  title: string;
}

export interface CashContractHealthSection {
  arOver60Days: MetricItem;
  dsoDays: MetricItem;
  subtitle: string;
  title: string;
}

export interface MarginDashboardResponse {
  cashContractHealth: CashContractHealthSection;
  hasPartnerLabor?: boolean;
  hasPendingHours: boolean;
  integrityScore: IntegrityScore;
  isPreStart: boolean;
  laborPressure: LaborPressureSection;
  marginIntegrity: MarginIntegritySection;
  operationalLeakage: OperationalLeakageSection;
  pendingHours: number;
  periodEnd: string;
  periodStart: string;
  projectId: string;
  summaryCards: SummaryCard[];
}

export interface MarginHealthSummary {
  color: 'GREEN' | 'AMBER' | 'RED';
  integrityScore: number;
  projectId: string;
}

const getMarginDashboard = async (params: {
  periodEnd: string;
  periodStart: string;
  projectId: string;
}): Promise<MarginDashboardResponse> => {
  const { projectId, periodStart, periodEnd } = params;
  const response = await axiosInstance.get(
    `/projects/${projectId}/margin-dashboard`,
    { params: { periodEnd, periodStart } }
  );
  return response.data;
};

const getMarginHealthBatch = async (params: {
  projects: string;
}): Promise<MarginHealthSummary[]> => {
  if (!params?.projects) return [];
  const response = await axiosInstance.get('/projects/margin-health-summary', {
    params: { projects: params.projects }
  });
  return response.data;
};

export default {
  getMarginDashboard,
  getMarginHealthBatch
};
